/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { RoomConfig, ChildGuest } from '../../types';
import { TFunction } from 'i18next';

export const formatChildAge = (age: number): string => {
  if (age === 0) return '<1 year old';
  return `${age} ${age === 1 ? 'year' : 'years'} old`;
};

/**
 * Generates an updated room array when child count changes
 */

export const getUpdatedChildrenArray = (
  currentChildren: ChildGuest[],
  newCount: number,
  defaultChildAge: number,
): ChildGuest[] => {
  let kids = [...currentChildren];
  if (newCount > kids.length) {
    for (let i = kids.length; i < newCount; i++) {
      kids.push({
        id: `child-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        age: defaultChildAge,
      });
    }
  } else {
    kids = kids.slice(0, newCount);
  }
  return kids;
};

/**
 * Generates a summary string for rooms and guests.
 */

export const getGuestSummary = (rooms: RoomConfig[], t: TFunction): string => {
  const { totalAdults, totalChildren } = rooms.reduce(
    (acc, room) => ({
      totalAdults: acc.totalAdults + room.adults,
      totalChildren: acc.totalChildren + room.children.length,
    }),
    { totalAdults: 0, totalChildren: 0 },
  );

  const totalInfants = rooms.reduce((acc, r) => acc + (r.infants || 0), 0);
  const totalChildrenAndInfants = totalChildren + totalInfants;

  const roomStr = `${rooms.length} ${t('searchHotelPage.guestSelection.room', { count: rooms.length })}`;
  const adultStr = `${totalAdults} ${t('searchHotelPage.guestSelection.adult', { count: totalAdults })}`;

  if (totalChildrenAndInfants > 0) {
    const childStr = `${totalChildrenAndInfants} ${t('searchHotelPage.guestSelection.child', { count: totalChildrenAndInfants })}`;
    return `${roomStr}, ${adultStr}, ${childStr}`;
  }

  return `${roomStr}, ${adultStr}`;
};

export const getChildAgeLabel = (age: number, t: TFunction): string => {
  return age === 0
    ? `< ${t('searchHotelPage.guestSelection.childAge', { count: 1 })}`
    : t('searchHotelPage.guestSelection.childAge', { count: age });
};
